import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/estoque/consumo-tinta-page.tsx — Fase 13, Plano 32 (EST-14, EST-15, D-01..D-04, D-06).
// Tela "Consumo de tinta" (`/estoque/consumo-tinta`): relatório previsto x real x perda por OP e lote e, na segunda aba, o
// rateio diário das cores de alto giro. Filtros, soma e paginação são do servidor (13-31); esta tela só apresenta.
// A aba ativa e os filtros do relatório moram na URL. A linha expandida mostra os movimentos da tinta no lote e a fórmula
// do real; cada movimento é uma `LinhaMovimentoTinta` que conhece o `movimentoId` e se foi estornado.
import { Fragment, useCallback, useEffect, useState, type FormEvent, type MouseEvent } from 'react';
import { AlertTriangle, CalendarClock, ChevronDown, ChevronRight, ClipboardList, Divide, FileClock, MoreHorizontal, RefreshCw, Scale, Search } from 'lucide-react';
import type { ClienteLookupPv, ConsumoTintaLinha, ListaConsumoTinta, MovimentoTintaLote, MovimentosTintaLote } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { FormMessage } from '../../components/ui/form-message';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { TablePagination } from '../../components/ui/table-pagination';
import { ClienteCombobox } from '../../components/vendas/cliente-combobox';
import { formatarQuantidadeEstoque } from '../../lib/estoque/formatacao';
import {
  TAMANHO_PAGINA_CONSUMO,
  TEXTO_RATEIO_PENDENTE,
  abaConsumoDaUrl,
  apresentacaoFonte,
  classeVariacao,
  escreverConsumoNaUrl,
  filtrosConsumoAtivos,
  filtrosConsumoVazios,
  lerFiltrosConsumoDaUrl,
  montarQueryConsumoTinta,
  periodoConsumoValido,
  rotuloModoPesagem,
  textoFormulaReal,
  textoLinhaRateio,
  textoVariacao,
  type AbaConsumoTinta,
  type FiltrosConsumoTinta,
  type IconeFonteConsumo,
} from '../../lib/estoque/consumo-tinta';
import { podeEstornarMovimento, rotuloMenuMovimento } from '../../lib/estoque/estorno-movimento';
import { PERMISSAO_APROVAR_AJUSTE } from '../../lib/estoque/inventario';
import { PERMISSAO_MOVIMENTAR_ESTOQUE } from '../../lib/estoque/movimentacao';
import { colunasVisiveis } from '../../lib/estoque/saldos';
import { obterSessaoAtual } from '../../lib/session';
import { formatarDataHora } from '../../lib/vendas/formatacao';
import { EstornarMovimentoTintaDialog } from './estornar-movimento-tinta-dialog';
import { APOIO_RATEIO_PENDENTE, RateioDiarioAba } from './rateio-diario-aba';

const ENDPOINT_CLIENTES_ESTOQUE = '/api/v1/estoque/lookups/clientes';
const APOIO_PREVISTO = 'Previsto vem do insumo do roteiro. Sem roteiro carregado, fica em branco.';
const CABECALHO = 'px-3 py-2 text-xs font-semibold uppercase tracking-wider';
const ROTULO = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const CONTROLE =
  'ui-control min-h-[var(--control-height,2rem)] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800';

const ICONES_FONTE: Readonly<Record<IconeFonteConsumo, typeof Scale>> = {
  Scale,
  Divide,
  ClipboardList,
  FileClock,
};

function buscaDaUrl(): string {
  return typeof window === 'undefined' ? '' : window.location.search;
}

function larguraDaJanela(): number {
  return typeof window === 'undefined' ? 1280 : window.innerWidth;
}

/** Largura atual da janela: Previsto, Perda e Fonte ficam na grade a partir de 1024px; abaixo vão para a linha expandida. */
function useGradeCompleta(): boolean {
  const [largura, setLargura] = useState(larguraDaJanela);
  useEffect(() => {
    const atualizar = () => setLargura(larguraDaJanela());
    window.addEventListener('resize', atualizar);
    return () => window.removeEventListener('resize', atualizar);
  }, []);
  return colunasVisiveis(largura).valor;
}

function chaveLinha(linha: ConsumoTintaLinha): string {
  return `${linha.loteId}-${linha.tintaMaterialId}`;
}

function contagem(total: number): string {
  return total === 1 ? '1 lote com tinta' : `${total} lotes com tinta`;
}

function kg(valor: number | null): string {
  return valor === null ? '—' : formatarQuantidadeEstoque(valor, 'kg');
}

function DadoExpandido({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className={ROTULO}>{rotulo}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

/** Badge da fonte do consumo: ícone(s) + texto (a cor nunca é o único sinal). */
export function FonteBadge({ fonte }: { fonte: ConsumoTintaLinha['fonte'] }) {
  const apresentacao = apresentacaoFonte(fonte);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${apresentacao.classe}`}>
      {apresentacao.icones.map((nome) => {
        const Icone = ICONES_FONTE[nome];
        return <Icone key={nome} className="size-3" aria-hidden="true" />;
      })}
      {apresentacao.rotulo}
    </span>
  );
}

function PerdaIndicador({ perdaKg, comTexto }: { perdaKg: number; comTexto: boolean }) {
  if (perdaKg <= 0) return comTexto ? <span className="text-muted-foreground">—</span> : null;
  const texto = formatarQuantidadeEstoque(perdaKg, 'kg');
  return (
    <span className="inline-flex items-center gap-1 text-amber-700">
      <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
      {comTexto ? <span className="tabular-nums">{texto}</span> : <span className="sr-only">Perda de {texto}</span>}
    </span>
  );
}

export interface LinhaMovimentoTintaProps {
  readonly movimento: MovimentoTintaLote;
  /** Célula extra à direita (menu "⋯" "Estornar movimento", plano 13-35). */
  readonly acoes?: React.ReactNode;
}

/** Uma linha da seção "Movimentos da tinta neste lote": identifica o movimento e mostra o badge "Estornado". */
export function LinhaMovimentoTinta({ movimento, acoes }: LinhaMovimentoTintaProps) {
  return (
    <tr data-movimento-id={movimento.movimentoId} data-estornado={movimento.estornado ? 'true' : 'false'} className="border-t">
      <td className="whitespace-nowrap py-1 pr-3 tabular-nums">{formatarDataHora(movimento.criadoEm)}</td>
      <td className="py-1 pr-3">
        <span className="inline-flex items-center gap-2">
          {rotuloModoPesagem(movimento.modo)}
          {movimento.estornado && (
            <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">Estornado</span>
          )}
        </span>
      </td>
      <td className={`whitespace-nowrap py-1 pr-3 text-right tabular-nums ${movimento.estornado ? 'text-muted-foreground line-through' : ''}`}>
        {formatarQuantidadeEstoque(movimento.pesoKg, 'kg')}
      </td>
      <td className="py-1 pr-3">{movimento.usuarioNome ?? '—'}</td>
      <td className="py-1 pr-3">{movimento.motivo ?? '—'}</td>
      {acoes !== undefined && <td className="py-1 text-right">{acoes}</td>}
    </tr>
  );
}

/** "Movimentos da tinta neste lote" e fórmula do real: lidos do servidor só quando a linha é expandida. */
interface DetalheLoteProps {
  readonly linha: ConsumoTintaLinha;
  readonly permissoes: readonly string[];
  /** Depois de um estorno: recarrega a linha do lote (real, variação, perda) sem recolher as linhas abertas. */
  readonly aoRecarregarLista: () => void;
  /** Mensagem de sucesso do estorno (a aba a anuncia em role="status"). */
  readonly aoAvisar: (mensagem: string) => void;
}

function DetalheLote({ linha, permissoes, aoRecarregarLista, aoAvisar }: DetalheLoteProps) {
  const [dados, setDados] = useState<MovimentosTintaLote | null>(null);
  const [erro, setErro] = useState(false);
  const [aEstornar, setAEstornar] = useState<MovimentoTintaLote | null>(null);
  const exibeAcoes = permissoes.includes(PERMISSAO_APROVAR_AJUSTE);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }) => {
      setErro(false);
      try {
        const params = new URLSearchParams({ tintaMaterialId: linha.tintaMaterialId });
        const res = await fetch(`/api/v1/estoque/consumo-tinta/lotes/${encodeURIComponent(linha.loteId)}?${params.toString()}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as MovimentosTintaLote;
        if (!sinal || sinal.vivo) setDados(json);
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      }
    },
    [linha.loteId, linha.tintaMaterialId],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  return (
    <section aria-label="Movimentos da tinta neste lote" className="flex flex-col gap-2">
      <h3 className={ROTULO}>Movimentos da tinta neste lote</h3>
      {erro ? (
        <div role="alert" className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-rose-700">Não foi possível carregar os movimentos deste lote.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void carregar()}>
            <RefreshCw aria-hidden="true" />
            Tentar novamente
          </Button>
        </div>
      ) : dados === null ? (
        <Skeleton className="h-8 w-full max-w-xl" />
      ) : (
        <>
          {dados.movimentos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum movimento por pesagem para esta tinta neste lote.</p>
          ) : (
            <table className="w-full max-w-3xl text-sm">
              <caption className="sr-only">Movimentos de {linha.tintaCodigo} no lote {linha.loteInternoPieltec}</caption>
              <thead className="text-left">
                <tr>
                  <th scope="col" className={`${ROTULO} py-1 pr-3`}>Data e hora</th>
                  <th scope="col" className={`${ROTULO} py-1 pr-3`}>Tipo</th>
                  <th scope="col" className={`${ROTULO} py-1 pr-3 text-right`}>Peso</th>
                  <th scope="col" className={`${ROTULO} py-1 pr-3`}>Usuário</th>
                  <th scope="col" className={`${ROTULO} py-1 pr-3`}>Motivo</th>
                  {exibeAcoes && (
                    <th scope="col" className="w-12 py-1">
                      <span className="sr-only">Ações</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {dados.movimentos.map((movimento) => (
                  <LinhaMovimentoTinta
                    key={movimento.movimentoId}
                    movimento={movimento}
                    acoes={
                      exibeAcoes
                        ? podeEstornarMovimento(permissoes, movimento) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button type="button" variant="ghost" size="icon" aria-label={rotuloMenuMovimento(movimento.criadoEm)}>
                                  <MoreHorizontal aria-hidden="true" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem className="min-h-10" onSelect={() => setAEstornar(movimento)}>
                                  Estornar movimento
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )
                        : undefined
                    }
                  />
                ))}
              </tbody>
            </table>
          )}
          {dados.movimentos.length > 0 && <p className="text-sm font-semibold tabular-nums">{textoFormulaReal(dados.saidasKg, dados.devolucoesKg)}</p>}
          {dados.rateios.length > 0 && (
            <ul className="flex flex-col gap-1 text-sm" aria-label="Consumo rateado por área">
              {dados.rateios.map((rateio) => (
                <li key={`${rateio.dia}-${rateio.tintaCodigo}`} className="inline-flex items-center gap-2">
                  <Divide className="size-3 shrink-0" aria-hidden="true" />
                  <span>
                    <span className="font-semibold">Rateio por área</span>: {textoLinhaRateio(rateio)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <EstornarMovimentoTintaDialog
        movimento={aEstornar}
        lote={linha}
        aberto={aEstornar !== null}
        aoFechar={() => setAEstornar(null)}
        aoEstornar={(mensagem) => {
          aoAvisar(mensagem);
          void carregar();
          aoRecarregarLista();
        }}
      />
    </section>
  );
}

interface LinhaConsumoProps {
  readonly linha: ConsumoTintaLinha;
  readonly aberta: boolean;
  readonly completa: boolean;
  readonly colSpan: number;
  readonly permissoes: readonly string[];
  readonly onAlternar: () => void;
  readonly aoRecarregarLista: () => void;
  readonly aoAvisar: (mensagem: string) => void;
}

function LinhaConsumo({ linha, aberta, completa, colSpan, permissoes, onAlternar, aoRecarregarLista, aoAvisar }: LinhaConsumoProps) {
  const idDetalhe = `consumo-${chaveLinha(linha)}-detalhe`;
  const apresentacao = apresentacaoFonte(linha.fonte);

  function cliqueNaLinha(event: MouseEvent<HTMLTableRowElement>): void {
    if ((event.target as HTMLElement).closest('a,button,[role="menuitem"]')) return;
    onAlternar();
  }

  return (
    <Fragment>
      <tr onClick={cliqueNaLinha} className="cursor-pointer border-t hover:bg-slate-50">
        <td className="w-12 px-1 py-1">
          <button
            type="button"
            onClick={onAlternar}
            aria-expanded={aberta}
            aria-controls={idDetalhe}
            aria-label={`${aberta ? 'Recolher' : 'Expandir'} consumo de ${linha.tintaCodigo} no lote ${linha.loteInternoPieltec}`}
            className="inline-flex size-11 items-center justify-center rounded-md hover:bg-slate-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          >
            {aberta ? <ChevronDown className="size-4" aria-hidden="true" /> : <ChevronRight className="size-4" aria-hidden="true" />}
          </button>
        </td>
        <td className="whitespace-nowrap px-3 py-2 tabular-nums">{linha.opNumero ?? '—'}</td>
        <td className="whitespace-nowrap px-3 py-2 font-semibold tabular-nums">{linha.loteInternoPieltec}</td>
        <td className="px-3 py-2">{linha.clienteNome ?? '—'}</td>
        <td className="px-3 py-2">
          <div className="font-semibold tabular-nums">{linha.tintaCodigo}</div>
          {linha.corCodigo && <div className="text-muted-foreground">{linha.corCodigo}</div>}
        </td>
        {completa && <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{kg(linha.previstoKg)}</td>}
        <td className="whitespace-nowrap px-3 py-2 text-right">
          <div className="inline-flex items-center justify-end gap-2">
            {!completa && <PerdaIndicador perdaKg={linha.perdaKg} comTexto={false} />}
            <span className="font-semibold tabular-nums">{kg(linha.realKg)}</span>
          </div>
          {linha.rateioPendente && (
            <div className="mt-1 flex justify-end">
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
                <CalendarClock className="size-3" aria-hidden="true" />
                {TEXTO_RATEIO_PENDENTE}
              </span>
            </div>
          )}
        </td>
        {completa && (
          <td className="whitespace-nowrap px-3 py-2 text-right">
            <PerdaIndicador perdaKg={linha.perdaKg} comTexto />
          </td>
        )}
        <td className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${classeVariacao(linha.variacaoKg)}`}>{textoVariacao(linha.variacaoKg)}</td>
        {completa && (
          <td className="px-3 py-2">
            <FonteBadge fonte={linha.fonte} />
          </td>
        )}
      </tr>
      {aberta && (
        <tr id={idDetalhe} className="border-t bg-muted/50">
          <td colSpan={colSpan} className="px-4 py-3">
            <div className="flex flex-col gap-4">
              <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                <DadoExpandido rotulo="Fonte do consumo">
                  <div className="flex flex-col gap-1">
                    <FonteBadge fonte={linha.fonte} />
                    <span className="text-muted-foreground">{apresentacao.detalhe}</span>
                  </div>
                </DadoExpandido>
                {!completa && <DadoExpandido rotulo="Previsto">{kg(linha.previstoKg)}</DadoExpandido>}
                {!completa && (
                  <DadoExpandido rotulo="Perda">
                    <PerdaIndicador perdaKg={linha.perdaKg} comTexto />
                  </DadoExpandido>
                )}
              </dl>
              {linha.previstoKg === null && !completa && <p className="text-sm text-muted-foreground">{APOIO_PREVISTO}</p>}
              {linha.rateioPendente && (
                <p className="inline-flex items-center gap-2 text-sm text-amber-700" role="note">
                  <CalendarClock className="size-4 shrink-0" aria-hidden="true" />
                  {APOIO_RATEIO_PENDENTE}
                </p>
              )}
              <DetalheLote linha={linha} permissoes={permissoes} aoRecarregarLista={aoRecarregarLista} aoAvisar={aoAvisar} />
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

function Cabecalho({ completa }: { completa: boolean }) {
  return (
    <thead className="sticky top-0 z-10 bg-slate-100 text-left">
      <tr>
        <th scope="col" className="w-12 px-1 py-2">
          <span className="sr-only">Expandir</span>
        </th>
        <th scope="col" className={CABECALHO}>OP</th>
        <th scope="col" className={CABECALHO}>Lote</th>
        <th scope="col" className={CABECALHO}>Cliente</th>
        <th scope="col" className={CABECALHO}>Tinta</th>
        {completa && (
          <th scope="col" className={`${CABECALHO} text-right`} aria-describedby="apoio-previsto">
            Previsto
          </th>
        )}
        <th scope="col" className={`${CABECALHO} text-right`}>Real</th>
        {completa && <th scope="col" className={`${CABECALHO} text-right`}>Perda</th>}
        <th scope="col" className={`${CABECALHO} text-right`}>Variação</th>
        {completa && <th scope="col" className={CABECALHO}>Fonte</th>}
      </tr>
    </thead>
  );
}

interface AbaLotesProps {
  readonly filtros: FiltrosConsumoTinta;
  readonly aoAlterarFiltros: (parcial: Partial<FiltrosConsumoTinta>) => void;
  readonly aoLimparFiltros: () => void;
  readonly aoAtualizarTotal: (total: number | null) => void;
  readonly permissoes: readonly string[];
}

function AbaPorOpELote({ filtros, aoAlterarFiltros, aoLimparFiltros, aoAtualizarTotal, permissoes }: AbaLotesProps) {
  const [buscaDigitada, setBuscaDigitada] = useState(filtros.busca);
  const [tintaDigitada, setTintaDigitada] = useState(filtros.tinta);
  const [cliente, setCliente] = useState<ClienteLookupPv | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(TAMANHO_PAGINA_CONSUMO);
  const [dados, setDados] = useState<ListaConsumoTinta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(new Set());
  const [aviso, setAviso] = useState<string | null>(null);
  const completa = useGradeCompleta();
  const periodoOk = periodoConsumoValido(filtros);
  const comFiltro = filtrosConsumoAtivos(filtros);

  const carregar = useCallback(
    async (sinal?: { vivo: boolean }, silencioso = false) => {
      if (!periodoOk) return;
      // Recarga silenciosa (depois de um estorno): mantém a grade e as linhas abertas; só atualiza os números.
      if (!silencioso) setCarregando(true);
      setErro(false);
      try {
        const res = await fetch(`/api/v1/estoque/consumo-tinta?${montarQueryConsumoTinta(filtros, { pagina, tamanhoPagina })}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as ListaConsumoTinta;
        if (!sinal || sinal.vivo) {
          setDados(json);
          if (!silencioso) setAbertas(new Set());
          aoAtualizarTotal(json.total);
        }
      } catch {
        if (!sinal || sinal.vivo) setErro(true);
      } finally {
        if (!sinal || sinal.vivo) setCarregando(false);
      }
    },
    [filtros, pagina, tamanhoPagina, periodoOk, aoAtualizarTotal],
  );

  useEffect(() => {
    const sinal = { vivo: true };
    void carregar(sinal);
    return () => {
      sinal.vivo = false;
    };
  }, [carregar]);

  function alterar(parcial: Partial<FiltrosConsumoTinta>): void {
    aoAlterarFiltros(parcial);
    setPagina(1);
  }

  function aplicarBusca(event: FormEvent): void {
    event.preventDefault();
    alterar({ busca: buscaDigitada.trim(), tinta: tintaDigitada.trim() });
  }

  function limpar(): void {
    setBuscaDigitada('');
    setTintaDigitada('');
    setCliente(null);
    aoLimparFiltros();
    setPagina(1);
  }

  function alternar(chave: string): void {
    setAbertas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });
  }

  // Cliente vindo da URL (sem o objeto do lookup): o combobox mostra um rótulo genérico até o usuário escolher outro.
  const clienteDoCombobox: ClienteLookupPv | null =
    filtros.clienteId === '' ? null : cliente?.id === filtros.clienteId ? cliente : { id: filtros.clienteId, codigo: '—', nome: 'Cliente filtrado' };
  const itens = dados?.itens ?? [];
  const colSpan = completa ? 10 : 7;
  const algumSemPrevisto = itens.some((item) => item.previstoKg === null);

  return (
    <div className="flex flex-col gap-6">
      <div className="sticky top-0 z-20 flex flex-col gap-3 border-b bg-background/95 py-3 backdrop-blur" role="group" aria-label="Filtros do relatório">
        <form onSubmit={aplicarBusca} role="search" className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              type="search"
              aria-label="Buscar por OP ou lote"
              placeholder="Buscar por OP ou lote"
              value={buscaDigitada}
              onChange={(e) => setBuscaDigitada(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit" variant="secondary">
            Buscar
          </Button>
        </form>

        <div className="flex flex-wrap items-end gap-3">
          <fieldset className="flex flex-wrap items-end gap-2">
            <legend className={`${ROTULO} mb-1`}>Período</legend>
            <div className="flex flex-col gap-1">
              <label htmlFor="consumo-de" className="text-xs text-muted-foreground">
                De
              </label>
              <DatePicker
                id="consumo-de"
                value={filtros.de}
                onValueChange={(value) => alterar({ de: value })}
                aria-invalid={!periodoOk}
                className={CONTROLE}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="consumo-ate" className="text-xs text-muted-foreground">
                Até
              </label>
              <DatePicker
                id="consumo-ate"
                value={filtros.ate}
                onValueChange={(value) => alterar({ ate: value })}
                aria-invalid={!periodoOk}
                className={CONTROLE}
              />
            </div>
          </fieldset>

          <div className="flex min-w-56 flex-col gap-1">
            <label htmlFor="consumo-cliente" className={ROTULO}>
              Cliente
            </label>
            <ClienteCombobox
              id="consumo-cliente"
              endpoint={ENDPOINT_CLIENTES_ESTOQUE}
              value={clienteDoCombobox}
              onChange={(escolhido) => {
                setCliente(escolhido);
                alterar({ clienteId: escolhido.id });
              }}
            />
          </div>

          <div className="flex min-w-48 flex-col gap-1">
            <label htmlFor="consumo-tinta" className={ROTULO}>
              Tinta ou cor
            </label>
            <form onSubmit={aplicarBusca}>
              <Input id="consumo-tinta" value={tintaDigitada} onChange={(e) => setTintaDigitada(e.target.value)} maxLength={60} />
            </form>
          </div>

          <label htmlFor="consumo-so-perda" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
            <Checkbox id="consumo-so-perda" checked={filtros.soComPerda} onCheckedChange={(marcada) => alterar({ soComPerda: marcada === true })} />
            Só lotes com perda
          </label>

          {comFiltro && (
            <Button type="button" variant="ghost" onClick={limpar}>
              Limpar filtros
            </Button>
          )}
        </div>
        {!periodoOk && <FormMessage>A data inicial não pode ser posterior à final.</FormMessage>}
      </div>

      {completa && algumSemPrevisto && (
        <p id="apoio-previsto" className="text-sm text-muted-foreground">
          {APOIO_PREVISTO}
        </p>
      )}
      {completa && !algumSemPrevisto && (
        <p id="apoio-previsto" className="sr-only">
          {APOIO_PREVISTO}
        </p>
      )}

      {aviso && (
        <p role="status" className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {aviso}
        </p>
      )}

      {erro ? (
        <Card>
          <CardContent role="alert" className="flex flex-col items-center gap-4 py-12 text-center">
            <p className="text-sm text-rose-700">Não foi possível carregar o consumo de tinta. Verifique a conexão e tente novamente.</p>
            <Button type="button" variant="outline" onClick={() => void carregar()}>
              <RefreshCw aria-hidden="true" />
              Tentar novamente
            </Button>
          </CardContent>
        </Card>
      ) : carregando ? (
        <div className="flex flex-col gap-2" aria-busy="true" aria-label="Carregando consumo de tinta">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : itens.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <h2 className="text-xl font-semibold leading-tight">Nenhum consumo de tinta no período</h2>
            <p className="max-w-xl text-sm text-muted-foreground">
              O consumo aparece quando o almoxarifado registra saídas por pesagem ou o rateio do dia é fechado.
            </p>
            {comFiltro && (
              <Button type="button" variant="outline" onClick={limpar}>
                Limpar filtros
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden py-0 gap-0">
          <table className="w-full text-sm">
            <caption className="sr-only">Consumo de tinta por OP e lote: previsto, real, perda e variação</caption>
            <Cabecalho completa={completa} />
            <tbody>
              {itens.map((linha) => (
                <LinhaConsumo
                  key={chaveLinha(linha)}
                  linha={linha}
                  aberta={abertas.has(chaveLinha(linha))}
                  completa={completa}
                  colSpan={colSpan}
                  permissoes={permissoes}
                  onAlternar={() => alternar(chaveLinha(linha))}
                  aoRecarregarLista={() => void carregar(undefined, true)}
                  aoAvisar={setAviso}
                />
              ))}
            </tbody>
          </table>
          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            totalItems={dados?.total ?? 0}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
            itemLabel="lotes"
          />
        </Card>
      )}
    </div>
  );
}

export function ConsumoTintaPage() {
  const [aba, setAba] = useState<AbaConsumoTinta>(() => abaConsumoDaUrl(buscaDaUrl()));
  const [filtros, setFiltros] = useState<FiltrosConsumoTinta>(() =>
    abaConsumoDaUrl(buscaDaUrl()) === 'lotes' ? lerFiltrosConsumoDaUrl(buscaDaUrl()) : filtrosConsumoVazios(),
  );
  const [total, setTotal] = useState<number | null>(null);
  const [permissoes, setPermissoes] = useState<readonly string[]>([]);

  // Permissões da sessão: só decidem o que é renderizado; a autorização real é do servidor.
  useEffect(() => {
    let vivo = true;
    void obterSessaoAtual().then((sessao) => {
      if (vivo) setPermissoes(sessao.autenticado ? sessao.usuario.permissions : []);
    });
    return () => {
      vivo = false;
    };
  }, []);

  // Aba e filtros persistem na URL (substitui a entrada do histórico: filtrar não empilha voltas).
  useEffect(() => {
    window.history.replaceState(null, '', `${window.location.pathname}${escreverConsumoNaUrl(aba, filtros)}`);
  }, [aba, filtros]);

  return (
    <div className="mx-auto w-full max-w-7xl p-6 flex flex-col gap-6">
      <Card>
        <CardContent className="flex flex-col gap-1">
          <h1 className="text-xl font-semibold leading-tight">Consumo de tinta</h1>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {aba === 'lotes' ? (total === null ? ' ' : contagem(total)) : ' '}
          </p>
        </CardContent>
      </Card>

      <Tabs value={aba} onValueChange={(valor) => setAba(valor === 'rateio' ? 'rateio' : 'lotes')} className="gap-6">
        <TabsList aria-label="Visões do consumo de tinta">
          <TabsTrigger value="lotes" className="min-h-9 px-4">
            Por OP e lote
          </TabsTrigger>
          <TabsTrigger value="rateio" className="min-h-9 px-4">
            Rateio diário
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lotes">
          <AbaPorOpELote
            filtros={filtros}
            aoAlterarFiltros={(parcial) => setFiltros((atual) => ({ ...atual, ...parcial }))}
            aoLimparFiltros={() => setFiltros(filtrosConsumoVazios())}
            aoAtualizarTotal={setTotal}
            permissoes={permissoes}
          />
        </TabsContent>

        <TabsContent value="rateio">
          <RateioDiarioAba podeMovimentar={permissoes.includes(PERMISSAO_MOVIMENTAR_ESTOQUE)} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
